import { createSlice } from "@reduxjs/toolkit";

/**
 * Network State Management
 *
 * Features:
 * - 5G support with "excellent" quality tier
 * - iOS-safe detection (no false warnings)
 * - Multi-level connection quality assessment
 */

interface NetworkState {
  isOnline: boolean;
  wasOffline: boolean;
  lastOnlineTimestamp: number | null;
  lastOfflineTimestamp: number | null;
  connectionType: "wifi" | "5g" | "4g" | "3g" | "slow-2g" | "unknown";
  effectiveType: "slow-2g" | "2g" | "3g" | "4g" | "5g" | "unknown";
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
  isNetworkAPISupported: boolean; // iOS detection
}

const getConnectionInfo = (): Pick<
  NetworkState,
  | "connectionType"
  | "effectiveType"
  | "downlink"
  | "rtt"
  | "saveData"
  | "isNetworkAPISupported"
> => {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) {
    return {
      connectionType: "unknown",
      effectiveType: "unknown",
      downlink: null,
      rtt: null,
      saveData: false,
      isNetworkAPISupported: false, // Mark as unsupported (iOS Safari)
    };
  }

  return {
    connectionType: connection.type || "unknown",
    effectiveType: connection.effectiveType || "unknown",
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false,
    isNetworkAPISupported: true, // Mark as supported
  };
};

const initialState: NetworkState = {
  isOnline: navigator.onLine,
  wasOffline: false,
  lastOnlineTimestamp: navigator.onLine ? Date.now() : null,
  lastOfflineTimestamp: !navigator.onLine ? Date.now() : null,
  ...getConnectionInfo(),
};

const networkSlice = createSlice({
  name: "network",
  initialState,
  reducers: {
    setOnline: (state) => {
      const wasOffline = !state.isOnline;
      state.isOnline = true;
      state.wasOffline = wasOffline;
      state.lastOnlineTimestamp = Date.now();
      Object.assign(state, getConnectionInfo());
    },
    setOffline: (state) => {
      state.isOnline = false;
      state.lastOfflineTimestamp = Date.now();
      Object.assign(state, getConnectionInfo());
    },
    updateConnectionInfo: (state) => {
      Object.assign(state, getConnectionInfo());
    },
    clearWasOfflineFlag: (state) => {
      state.wasOffline = false;
    },
  },
});

export const {
  setOnline,
  setOffline,
  updateConnectionInfo,
  clearWasOfflineFlag,
} = networkSlice.actions;

export default networkSlice.reducer;

// SELECTORS

export const selectIsOnline = (state: { network: NetworkState }) =>
  state.network.isOnline;

export const selectWasOffline = (state: { network: NetworkState }) =>
  state.network.wasOffline;

// QUALITY ASSESSMENT: 5-tier system
export const selectConnectionQuality = (state: {
  network: NetworkState;
}): "excellent" | "good" | "moderate" | "poor" | "unavailable" => {
  const { effectiveType, rtt, downlink, isNetworkAPISupported } = state.network;

  // iOS or unsupported browsers
  if (!isNetworkAPISupported) {
    return "unavailable";
  }

  // Excellent: 5G or very high performance
  if (
    effectiveType === "5g" ||
    (downlink !== null && downlink > 50) || // > 50 Mbps
    (rtt !== null && rtt < 50) // < 50ms latency
  ) {
    return "excellent";
  }

  // Good: 4G with low latency
  if (effectiveType === "4g" && (!rtt || rtt < 100)) {
    return "good";
  }

  // Moderate: 3G or acceptable latency
  if (effectiveType === "3g" || (rtt && rtt < 300)) {
    return "moderate";
  }

  // Poor: 2G or slow connections
  return "poor";
};

export const selectShouldUseCache = (state: {
  network: NetworkState;
}): boolean => {
  return !state.network.isOnline || state.network.saveData;
};

// iOS-SAFE: Only show warnings for genuinely poor connections
export const selectShouldShowConnectionWarning = (state: {
  network: NetworkState;
}): boolean => {
  const { isNetworkAPISupported, effectiveType, rtt, downlink } = state.network;

  // Don't show warnings if API isn't supported (iOS)
  if (!isNetworkAPISupported) {
    return false;
  }

  // Only show if connection is genuinely poor
  return (
    effectiveType === "slow-2g" ||
    effectiveType === "2g" ||
    (rtt !== null && rtt > 500) ||
    (downlink !== null && downlink < 0.5) // < 500 Kbps
  );
};

// HUMAN-READABLE LABELS
export const selectConnectionTypeLabel = (state: {
  network: NetworkState;
}): string => {
  const { effectiveType, isNetworkAPISupported } = state.network;

  if (!isNetworkAPISupported) {
    return "Unknown";
  }

  switch (effectiveType) {
    case "5g":
      return "5G";
    case "4g":
      return "4G";
    case "3g":
      return "3G";
    case "2g":
      return "2G";
    case "slow-2g":
      return "Slow 2G";
    default:
      return "Unknown";
  }
};

// CHECK IF FAST ENOUGH FOR LARGE OPERATIONS
export const selectCanHandleLargeDownloads = (state: {
  network: NetworkState;
}): boolean => {
  const quality = selectConnectionQuality(state);
  return quality === "excellent" || quality === "good";
};
